import { apm } from '@elastic/apm-rum';
import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { getColumnMarketGroups, getColumnMarketType, getDisplayMarketTypeInfos, getNewBetTypeDisplayInfos, toBetBuilderMarketTypeInfos } from '@/components/oddsDisplay/marketTypeDisplayRule';
import { useDynamicMarket } from '@/composable/useDynamicMarket';
import { useShowTimeDisplay } from '@/composable/useShowTimeDisplay';
import { getMarketGroupId, isFastMarket, isMainMarket, isNextGoal } from '@/core/lib/oddsHelper';
import { getTranslated } from '@/core/lib/translationHelper';
import { getLiveTimeDisplay } from '@/core/oddsApi/helpers/liveScore';
import type { IEvent, IEventEuro } from '@/interface/IEvent';
import type { IEventLike } from '@/interface/IEventLike';
import type { MarketColumnEntry } from '@/interface/IMarketTypeInfo';
import type { IEventResult, IOdds } from '@/interface/IOdds';
import type { IBetBuilderMarket } from '@/interface/betBuilder';
import type { OddsDisplayMode } from '@/interface/enum';
import { BetTypeGroup, HandicapType, MarketShowingType, MarketType, SportType, TeamOption } from '@/interface/enum';
import { useCustomerStore } from '@/store/customerStore';
import { useOddsDisplaySettingsStore } from '@/store/oddsDisplaySettingsStore';

type EventType = IEvent | IEventEuro;

export const mainMarketMoreOrder = [
    MarketType.LiveScore,
    MarketType.FH_LiveScore,
    MarketType.MoneyLine,
    MarketType.Handicap,
    MarketType.FH_Handicap,
    MarketType.OverUnder,
    MarketType.FH_OverUnder,
    MarketType._1X2,
    MarketType.FH_1X2,
    MarketType.OddEven,
    MarketType.FH_OddEven,
    MarketType.TotalGoal,
    MarketType.DoubleChance,
    MarketType.CorrectScore,
    MarketType.FH_CorrectScore,
    MarketType.SH_CorrectScore,
    MarketType.HTFT,
    MarketType.FGLG,
];

function isShowOnMainMarket(_odds: MaybeRef<IOdds>, marketTypeInfos: MarketColumnEntry[], displayMarketGroup: BetTypeGroup) {
    const odds = get(_odds);
    if (isMainMarket(displayMarketGroup)) {
        if (isNextGoal(odds)) return marketTypeInfos.some(x => getColumnMarketGroups(x).some(group => isNextGoal(group)));
        return marketTypeInfos.some(x => getColumnMarketGroups(x).includes(odds.eventResult.marketGroup.id) && getColumnMarketType(x) === odds.marketType);
    }
    return marketTypeInfos.filter(x => getColumnMarketGroups(x).some(group => isMainMarket(group))).some(x => getColumnMarketType(x) === odds.marketType);
}

export function getMarketOdds(oddsDisplayMode: OddsDisplayMode, _event: MaybeRef<IEvent>, displayMarketGroup: BetTypeGroup, _oddsList: MaybeRef<IOdds[]> = []) {
    const event = get(_event);
    const oddsList = get(_oddsList);
    const mainMarketTypeInfos = getDisplayMarketTypeInfos(event.sportType, event.isLive, oddsDisplayMode);

    const newBetTypeColumnConfig = isMainMarket(displayMarketGroup)
        ? undefined
        : getNewBetTypeDisplayInfos(event.sportType, displayMarketGroup, event.isLive);

    if (newBetTypeColumnConfig) {
        const shownMarketTypes = newBetTypeColumnConfig
            .filter((x): x is MarketColumnEntry => x !== null)
            .map(x => getColumnMarketType(x));
        return {
            main: oddsList.filter(odds => shownMarketTypes.includes(odds.marketType)),
            more: oddsList.filter(odds => !shownMarketTypes.includes(odds.marketType)),
        };
    }

    return {
        main: oddsList.filter(odds => isShowOnMainMarket(odds, mainMarketTypeInfos, displayMarketGroup)),
        more: oddsList.filter(odds => !isShowOnMainMarket(odds, mainMarketTypeInfos, displayMarketGroup)),
    };
}

export function getBetBuilderDisplayMarkets(oddsDisplayMode: OddsDisplayMode, _match: MaybeRef<IEvent>, _markets: MaybeRef<IBetBuilderMarket[]>) {
    const match = get(_match);
    const markets = get(_markets);
    const mainMarketTypeInfos = getDisplayMarketTypeInfos(match.sportType, match.isLive, oddsDisplayMode);
    const betBuilderDisplayMarketTypeInfos = toBetBuilderMarketTypeInfos(mainMarketTypeInfos);

    const isShowOnMainMarket = (market: IBetBuilderMarket) => betBuilderDisplayMarketTypeInfos.some(x => x && x.betBuilderMarketType === market.marketType.id && x.statType === market.matchStatType.id);

    return {
        main: markets.filter(market => isShowOnMainMarket(market)),
        more: markets.filter(market => !isShowOnMainMarket(market)),
    };
}

export function useMoreMarketOddsDisplay(oddsDisplayMode: OddsDisplayMode, event: MaybeRef<IEvent>, oddsList: MaybeRef<IOdds[]> = []) {
    const displayMainMarketTypeInfos = getDisplayMarketTypeInfos(get(event).sportType, get(event).isLive, oddsDisplayMode);

    // more market will show those odds which is *not* in displayMarketTypes
    const mainMarketOddsList = computed(() => {
        const filteredOdds = get(oddsList)
            .filter(odds => isMainMarket(odds))
            .filter(odds => !displayMainMarketTypeInfos.some(x => getColumnMarketGroups(x).includes(odds.eventResult.marketGroup.id) && getColumnMarketType(x) === odds.marketType));

        return filteredOdds;
    });

    const fastMarketOddsList = computed(() => get(oddsList).filter(odds => isFastMarket(odds)));

    const newBetTypeOddsList = computed(() => {
        const filteredOdds = get(oddsList)
            .filter(odds => !isMainMarket(odds))
            .filter(odds => !isFastMarket(odds));
        return filteredOdds;
    });

    return {
        mainMarketOddsList,
        newBetTypeOddsList,
        fastMarketOddsList,
    };
}

function isShownOnMainMarket(
    mainMarketTypeInfos: ReturnType<typeof getDisplayMarketTypeInfos>,
    marketGroup: BetTypeGroup,
    marketType: MarketType,
) {
    const nextGoalGroup = isNextGoal(marketGroup);

    return mainMarketTypeInfos.some(x => (nextGoalGroup
        ? getColumnMarketGroups(x).some(group => isNextGoal(group))
        : getColumnMarketType(x) === marketType && getColumnMarketGroups(x).includes(marketGroup)));
}

export function getMoreMarketCount(oddsDisplayMode: OddsDisplayMode, event: IEvent, displayMarketGroup: BetTypeGroup) {
    const mainMarketTypeInfos = getDisplayMarketTypeInfos(event.sportType, event.isLive, oddsDisplayMode);
    const dynamicMarketCount = isMainMarket(displayMarketGroup) ? event.dynamicMarketCount ?? 0 : 0;

    const newBetTypeColumnConfig = isMainMarket(displayMarketGroup)
        ? undefined
        : getNewBetTypeDisplayInfos(event.sportType, displayMarketGroup, event.isLive);
    const shownMarketTypes = newBetTypeColumnConfig
        ? newBetTypeColumnConfig.filter((x): x is MarketColumnEntry => x !== null)
        : mainMarketTypeInfos;

    if (!event.marketTypeCount) {
        apm.captureError(new Error(`[getMoreMarketCount] marketTypeCount is null/undefined, eventId=${event.id}`));
        return 0;
    }

    const marketTypeCount = Object.entries(event.marketTypeCount).reduce((marketGroupsSum, [marketGroupString, value]) => {
        const marketGroup = Number.parseInt(marketGroupString) as BetTypeGroup;
        if (!value) {
            apm.captureError(new Error(`[getMoreMarketCount] inner value is null/undefined, eventId=${event.id} marketGroupString=${marketGroupString}`));
            return marketGroupsSum;
        }
        return Object.entries(value).reduce((marketTypesSum, [marketTypeString, count]) => {
            const marketType = Number.parseInt(marketTypeString) as MarketType;
            count = count ?? 0;

            if (isMainMarket(displayMarketGroup)) {
                if (isShownOnMainMarket(mainMarketTypeInfos, marketGroup, marketType)) {
                    return marketTypesSum;
                }
                if (isShowNewBetTypeOnMainPage(event.sportType, marketGroup, event.isLive, oddsDisplayMode)) {
                    return marketTypesSum;
                }
                return marketTypesSum + count;
            }

            if (marketGroup === displayMarketGroup) {
                const isShown = newBetTypeColumnConfig
                    ? shownMarketTypes.some(x => getColumnMarketType(x) === marketType)
                    : shownMarketTypes.some(x => getColumnMarketGroups(x).includes(BetTypeGroup.Main) && getColumnMarketType(x) === marketType);
                if (isShown) {
                    return marketTypesSum;
                }
                return marketTypesSum + count;
            }

            return marketTypesSum;
        }, marketGroupsSum);
    }, 0);

    const { isDynamicMarketEnabled } = useDynamicMarket();

    if (isDynamicMarketEnabled.value) {
        return marketTypeCount + dynamicMarketCount;
    }
    return marketTypeCount;
}

export function isShowNewBetTypeOnMainPage(sportType: SportType, marketGroupId: BetTypeGroup, isLive: boolean, oddsDisplayMode: OddsDisplayMode): boolean {
    const { marketShowing } = useOddsDisplaySettingsStore();
    if (marketShowing === MarketShowingType.Main) return false;

    if (!isLive) return false;
    if (isMainMarket(marketGroupId)) return false;
    if (isFastMarket(marketGroupId)) return false;
    if (isNextGoal(marketGroupId)) return false;
    if (sportType === SportType.Cricket) return false;

    const isShowOnMainEvent = getDisplayMarketTypeInfos(sportType, isLive, oddsDisplayMode).some(x => getColumnMarketGroups(x).includes(marketGroupId));
    return !isShowOnMainEvent;
}

export function isHomeFavorite(eventResult: IEventResult) {
    return eventResult?.handicapType === HandicapType.Home;
}

export function isAwayFavorite(eventResult: IEventResult) {
    return eventResult?.handicapType === HandicapType.Away;
}

/** Main market handicap decides the favorite team; handicap of other market groups is the fallback */
export function getHandicapEventResult(oddsList: IOdds[]) {
    const handicapOddsList = oddsList.filter(odds => [MarketType.Handicap, MarketType.FH_Handicap].includes(odds.marketType));
    const handicapOdds = handicapOddsList.find(odds => getMarketGroupId(odds) === BetTypeGroup.Main) ?? handicapOddsList[0];
    return handicapOdds?.eventResult;
}

export function getTeamName(teamOption: TeamOption, event: EventType, oddsList: IOdds[]) {
    return translateNewBetTypeTemplate(teamOption, event, oddsList);
}

function translateNewBetTypeTemplate(teamOption: TeamOption, event: EventType, oddsList: IOdds[]) {
    if (oddsList.length === 0) {
        switch (teamOption) {
            case TeamOption.Home:
                return getTranslated(event.homeTeam.name);
            case TeamOption.Away:
                return getTranslated(event.awayTeam.name);
            default:
                return '';
        }
    }

    return replaceTemplate(teamOption, event, oddsList[0]);
}

function replaceTemplate(teamOption: TeamOption, event: EventType, odds: IOdds) {
    const { marketGroup } = odds.eventResult;
    const template = teamOption === TeamOption.Home
        ? marketGroup.homeTemplate
        : marketGroup.awayTemplate;
    return template
        .replace(/_/g, '')
        .replace(/{home}/ig, getTranslated(event.homeTeam.name))
        .replace(/{homeext}/ig, getTranslated(marketGroup.homeExtension))
        .replace(/{away}/ig, getTranslated(event.awayTeam.name))
        .replace(/{awayext}/ig, getTranslated(marketGroup.awayExtension));
}

export function isDisplayEventDate(event: IEvent) {
    return !event.isSboLive && !event.isTvLive;
}

export function getNonLiveTimeDisplay(event: IEvent) {
    const { date, time } = useShowTimeDisplay(event.showTime, event.showTimeType);
    return {
        date: event.isSboLive ? '' : date,
        time,
        showLiveTag: event.isSboLive,
    };
}

export function getMatchLiveTimeDisplay(event: IEventLike) {
    if (!event.isLive) return '';
    const period = event.extraInfo?.period ?? 0;
    const periodStartTime = event.extraInfo?.periodStartTime ?? '';
    const { siteStyle } = storeToRefs(useCustomerStore());

    return getLiveTimeDisplay(period, periodStartTime, siteStyle.value);
}

export function isShowInjuryTime(event: IEventLike) {
    return (event.extraInfo?.injuryTime ?? 0) > 0;
}
